/**
 * Adapter: ParsedEstimateResponse -> ScopeTreeData, moved out of
 * V2SupplementPage (god-file ratchet) when PRP-MCP-5 added the chat marks.
 */
import type { GroupNode, ParsedEstimateResponse } from '../../types';
import type { ScopeTreeData, ScopeTreeItem, TreeNode } from '../components/ScopeTreeTable';
import { activitySymbol } from './semantics';
import { isChatLine, lineSuffixOf, roomHasChat } from './chatChanges';
import type { ChatMarks } from './chatChanges';

/**
 * Builds a {@link ScopeTreeData} from a parsed estimate response so the
 * reusable {@link ScopeTreeTable} can render the parsed rooms.
 *
 * Rooms become top-level tree nodes (keyed by room group_code, labelled by
 * group_description). Line items are flattened with `area` = room id,
 * `heading` = section heading (from room.headings positioned within the
 * line items) or the room description as a fallback. CAT/SEL/Act/Qty/Unit/Desc
 * come straight from each parsed line item; `note` is the joined `notes[]`.
 *
 * `total` is the carrier's line RCV recovered from the PDF (B-159), or 0 when
 * the source line had no money columns — never fabricated. The footer in
 * ScopeTreeTable notes that totals are recalculated in Xactimate on export.
 * A line with a `price_note` is zero and unpriced even with an `rcv` underneath (a line
 * marked after import keeps its imported price), and leaves its folders' printed subtotals.
 *
 * @param res - The parsed estimate response from the backend.
 * @returns The tree-table data shape.
 */
export function parsedToTreeData(res: ParsedEstimateResponse, marks?: ChatMarks): ScopeTreeData {
  const meta = res.estimate.metadata;
  const tree: TreeNode[] = [];
  const items: ScopeTreeItem[] = [];
  let runningN = 1;
  // PRP-MCP-7 G7: in a priced estimate, a line with no RCV (chat changed or added it) is unpriced.
  const priced = res.estimate.rooms.some((r) => r.line_items.some((li) => li.rcv != null));

  // Registry of container (folder) nodes keyed by their cumulative path, so
  // rooms sharing a chain (e.g. Building > Main Areas > Unit 105) reuse the same
  // nodes instead of duplicating the tree. Step 1 slice B.
  const containers = new Map<string, TreeNode>();

  /**
   * Find-or-create the recovered ancestor chain and return the children array
   * the room leaf belongs in — the innermost container's children, or the
   * top-level tree for a flat estimate (no group_path).
   */
  const childrenForPath = (path: GroupNode[] | undefined, chain: TreeNode[]): TreeNode[] => {
    if (!path || path.length === 0) return tree;
    let siblings = tree;
    let key = '';
    for (const node of path) {
      key = key ? `${key} > ${node.name}` : node.name;
      let container = containers.get(key);
      if (!container) {
        container = {
          id: `grp:${key}`,
          label: node.name,
          children: [],
          subtotal: node.subtotal_rcv ?? null,
        };
        containers.set(key, container);
        siblings.push(container);
      }
      chain.push(container);
      siblings = container.children!;
    }
    return siblings;
  };

  res.estimate.rooms.forEach((room, roomIdx) => {
    // PRP-MCP-5: "Only chat changes" keeps just the rooms and lines chat touched.
    if (marks?.onlyChat && !roomHasChat(marks.index, room)) return;
    const areaId = room.group_code || `room-${roomIdx}`;
    const roomLabel = room.group_description || room.group_code || `Room ${roomIdx + 1}`;
    const chain: TreeNode[] = [];
    childrenForPath(room.group_path, chain).push({
      id: areaId,
      label: roomLabel,
      fromChat: !!marks?.index.rooms.has(room.group_code),
    });

    // Build a position -> heading map so each line item can resolve the
    // closest preceding section heading (B-039 headings carry a `position`).
    const headings = [...(room.headings ?? [])].sort((a, b) => a.position - b.position);
    const headingAt = (position: number): string => {
      let current = roomLabel;
      for (const h of headings) {
        if (h.position <= position) current = h.text;
        else break;
      }
      return current;
    };

    room.line_items.forEach((li, liIdx) => {
      const fromChat = !!marks && isChatLine(marks.index, room, li);
      if (li.price_note && li.rcv) {
        for (const c of chain) if (c.subtotal != null) c.subtotal -= li.rcv;
      }
      if (marks?.onlyChat && !fromChat) return;
      items.push({
        id: `${areaId}-${li.line_number ?? liIdx}${lineSuffixOf(li)}`,
        fromChat,
        area: areaId,
        heading: headingAt(liIdx),
        n: runningN++,
        cat: li.category || '—',
        sel: li.selector || '—',
        act: activitySymbol(li.activity ?? ''),
        qty: li.quantity ?? null,
        unit: li.unit || '',
        // B-159: show the carrier's line total (RCV) so the review screen
        // matches the priced ESX export. null/0 when the source had no pricing
        // — still never fabricated.
        total: li.price_note ? 0 : (li.rcv ?? 0),
        unpriced: !!li.price_note || (priced && li.rcv == null),
        priceNote: li.price_note ?? null,
        roomIdx,
        itemIdx: liIdx,
        desc: li.description || '',
        // PRP-MCP-5: chat lines carry a calc (W, C, PF); the CALC column showed "—" for them.
        // PRP-MCP-4: the room variable (W, C, PF*4) the quantity comes from. Read
        // here, not typed in types/index.ts, which is at its size limit.
        calc: (li as { calc_expression?: string | null }).calc_expression || null,
        note: li.notes && li.notes.length > 0 ? li.notes.join(' ') : undefined,
      });
    });
  });

  return {
    name: meta.estimate_name || 'Imported estimate',
    sub: meta.property_address || 'Faithful import',
    priceList: meta.price_list || '—',
    ref: meta.damage_type || 'Import',
    tree,
    items,
  };
}
